/**
 * The Roku page's content, as data.
 *
 * Every picture on the page is a real capture off a Roku Express 4K+ (the
 * Developer Tool's screenshot, 1920x1080, untouched) and lives under
 * `apps/web/public/roku/`, with a 480px thumbnail beside it in `thumbs/`.
 * The captions describe what is on screen in that frame, so a reviewer can
 * hold one against the other.
 */

/** One capture in the gallery. */
export interface RokuShot {
  /** Stable key; also the file stem under `/roku/` and `/roku/thumbs/`. */
  id: string;
  /** The game or screen, as the filmstrip labels it. */
  title: string;
  /** One or two sentences about this exact frame. */
  caption: string;
  /** Alt text: what a screen reader should hear in place of the picture. */
  alt: string;
}

export const ROKU_SHOTS: readonly RokuShot[] = [
  {
    id: 'roulette',
    title: 'Roulette',
    caption:
      'A single-zero wheel beside the full layout. Each chip on the board wears its player’s color, and the panel on the left tracks the last spins and the hot and cold numbers.',
    alt: 'Roulette on the Roku: the wheel, the betting layout with a blue chip on 7 and a gold chip on Black, and a recent-spins panel.',
  },
  {
    id: 'craps',
    title: 'Craps',
    caption:
      'The point is 6. Ava rolls a 5, so her pass line chip and Ben’s place bet on the six both stay up. The roll history runs under the seats.',
    alt: 'Craps on the Roku: a POINT 6 marker, two dice showing 1 and 4, and the full craps layout with chips on Pass Line and Six.',
  },
  {
    id: 'blackjack-win',
    title: 'Blackjack',
    caption:
      'Dealer draws to 19. Ben’s ace-queen pays 3 to 2, Cleo pushes, Ava’s 15 loses, and the win banner nets Ben’s side bet: +145.',
    alt: 'Blackjack on the Roku: the dealer shows 5, 7, 7 and a Big Win banner reads +145 for Ben above three player seats.',
  },
  {
    id: 'baccarat',
    title: 'Baccarat',
    caption:
      'Player draws to 9 and beats Banker’s 5. Ben backed Player and takes +40. The bead road under the seats keeps the shoe.',
    alt: 'Baccarat on the Roku: Player hand 4, 9, 6 totals 9 and Banker hand 9, 5, A totals 5, with three seats and their results.',
  },
  {
    id: 'holdem',
    title: 'Texas Hold’em',
    caption:
      'Aces on the flop. The TV shows the board and the pot, and hole cards stay face down. Each player reads theirs on their own phone.',
    alt: 'Texas Hold’em on the Roku: a flop of A, 8, A, pot 30, and three seats with face-down hole cards.',
  },
  {
    id: 'keno',
    title: 'Keno',
    caption:
      'Players pick their spots on their phones, and the TV rings each ticket in its owner’s color before the house draws twenty.',
    alt: 'Keno on the Roku: an 80-number board with Ava’s six spots ringed in blue and Ben’s four in gold.',
  },
  {
    id: 'leaderboard',
    title: 'Leaderboard',
    caption:
      'Between rounds the TV turns into the house board: chip counts, a line of each player’s night, their best hand and their home table.',
    alt: 'The leaderboard on the Roku: three ranked player cards with chip totals, sparklines and stats.',
  },
  {
    id: 'lobby',
    title: 'Lobby',
    caption:
      'The table before anyone sits. Scan the QR or type the four-letter code, and whoever takes the first seat deals from their phone.',
    alt: 'The Roku lobby: a large join code GCJD beside a QR code, and an empty leaderboard.',
  },
];

/** Where the full-size capture and its thumbnail live in the web app. */
export const shotSrc = (shot: RokuShot) => `/roku/${shot.id}.jpg`;
export const shotThumb = (shot: RokuShot) => `/roku/thumbs/${shot.id}.jpg`;

/** The gallery's index after a step, wrapping at both ends. */
export const stepShot = (index: number, by: number, count = ROKU_SHOTS.length) =>
  (((index + by) % count) + count) % count;

/**
 * Getting the channel, in the order a first-timer meets it. The page is
 * written as if the channel is in the Roku Streaming Store; the channel boots
 * onto the live house (https://tvcasinonight.com) without asking.
 */
export interface SetupStep {
  title: string;
  body: string;
}

export const SETUP_STEPS: readonly SetupStep[] = [
  {
    title: 'Add the channel',
    body: 'On your Roku, open the Streaming Store, search for TV Casino Night, and choose Add channel. It\u2019s free.',
  },
  {
    title: 'Open it',
    body: 'The channel connects on its own and puts a join code and a QR on the TV. There is nothing to sign in to.',
  },
  {
    title: 'Scan in from the couch',
    body: 'Everyone points their phone camera at the QR, or types the code. Whoever takes the first seat deals.',
  },
];
